/**
 * DealDetail.js — one deal, on its own page
 * =========================================
 * Usability review of 23 October 2026:
 *
 *   item 22  "Open the deal detail beside the list, or on its own page, rather
 *            than further down the same long page. Right now it isn't obvious
 *            which deal is selected or that a detail view exists below."
 *   item 23  "Split the deal detail into tabs: Summary, Cash curve, Tranches,
 *            Activity, Documents. The Valuno report runs to hundreds of rows
 *            on one page."
 *
 * Brian, 29 October 2026, added the sixth, Investors: "the investor waterfall
 * with the full breakdown of cash deployed, cash returned, cash distributed,
 * etc." (`./Investors.js`).
 *   item 3   a deal links back to its position, its ledger and its notices.
 *
 * So a deal is `/deal-reports/<id>`: its name is the page's heading, the way
 * back to the list is the first thing on it, Previous and Next step through
 * the list as it was left, and the tab is in the address (`?tab=activity`), so
 * a reload or a link lands on the same view. The warnings that change how
 * every figure reads — a missing exchange rate, an incomplete cost basis — sit
 * above the tabs, because they are true of all five.
 */
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Card, PageHeader, Tabs } from "../../components/ui";
import { InfoTip, ToolRow } from "../../components/PageTools";
import { multiple, pct, toNumber } from "../../components/format";
import { dealReportPath, ledgerPath, noticeReviewPath, positionsPath } from "../../components/links";
import theme from "../../components/theme";
import { currencyOf, day, dealMoney, dealSubline, viewOf } from "./figures";
import { strategyLabel } from "./listing";
import { basisReasons } from "./FundSummary";
import { CashCurve } from "./Curve";
import { FundingRounds, showFundingRounds, singleTrancheText } from "./FundingRounds";
import { PnlBridge } from "./PnlBridge";
import { DealActivity } from "./Activity";
import { DealDocuments } from "./Documents";
import { InvestorWaterfall } from "./Investors";
import { DealPdfButton } from "./pdf";
import { Tile, toneOfValue } from "./Tiles";

export const DEAL_TABS = [
  { key: "summary", label: "Summary" },
  { key: "investors", label: "Investors" },
  { key: "curve", label: "Cash curve" },
  { key: "tranches", label: "Tranches" },
  { key: "activity", label: "Activity" },
  { key: "documents", label: "Documents" },
];

export const tabOf = (search) => {
  const wanted = search.get("tab");
  return DEAL_TABS.some((t) => t.key === wanted) ? wanted : "summary";
};

// A correction that moved a wire's position into the clearing account is read
// with the wire, as the transfer the two add up to (deal_cash_series
// `_repointings`). Said beside the reversals, because both are journals the
// figures leave out on purpose.
export function repointedText(deal) {
  const n = deal.repointed.transactions;
  return (
    `${n} ${n === 1 ? "correction" : "corrections"} that moved a wire's position into ` +
    `the deal's clearing account ${n === 1 ? "is" : "are"} read with the wire as one ` +
    `transfer, carrying ${dealMoney(deal.repointed.cash, currencyOf(deal))}. Read alone, ` +
    "each would count the same money out twice and back once."
  );
}

function BasisWarning({ deal }) {
  if (deal.summary.basis_complete) return null;
  const bits = basisReasons(deal);
  return (
    <div className="dr-warning" style={{ marginBottom: theme.space.lg }}>
      <strong style={{ color: theme.text }}>Cost basis is incomplete.</strong> Net cash is not a
      return for this deal yet — uncosted disposals book their whole proceeds as gain.
      {bits.length > 0 && (
        <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
          {bits.map((bit) => (
            <li key={bit} style={{ marginBottom: 2 }}>
              {bit}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UnconvertedWarning({ deal }) {
  if (!viewOf(deal).unconverted) return null;
  const list = (deal.currency || []).join(" and ");
  return (
    <div className="dr-warning" style={{ marginBottom: theme.space.lg }}>
      <strong style={{ color: theme.text }}>These figures are not in one currency.</strong> This deal
      moved in {list}, and an exchange rate is missing for one of its dates, so the curve and its
      figures add the currencies at face value. The rates are loaded each morning with the prices;
      the figures convert once the missing one is in.
    </div>
  );
}

// What came back, and what was paid out of it that is not capital, when
// anything was (D-285).
export function cashBackNote(stats, currency) {
  const paid = toNumber(stats && stats.paid_out_not_capital);
  if (!paid) return "shares sold, notes repaid, interest and fees";
  return `${dealMoney(stats.cash_returned, currency)} back, less ${dealMoney(paid, currency)} paid out of it that was not capital`;
}

// The figures a reader came for, with what each rests on under it.
function SummaryTiles({ deal }) {
  const stats = deal.stats;
  const { currency, summary } = viewOf(deal);
  const c = (stats && stats.currency) || currency;
  const withheld = !stats || stats.measured === false;
  const floor = stats && stats.carrying_is_negative;
  const provisional = deal.summary && !deal.summary.basis_complete;
  const why = withheld ? (stats ? stats.withheld_because : "no figures for this deal") : null;
  const figure = (key, spell) => (withheld || toNumber(stats[key]) === null ? "n/a" : spell(stats[key]));
  const heldNote =
    stats && stats.value_basis === "marked" ? "what is still held is marked to its price" : "what is still held is at cost";
  const valueNote = floor
    ? "a floor: the investment accounts are below zero, so this counts the cash only"
    : provisional
    ? "provisional: some disposals have no cost loaded"
    : heldNote;
  return (
    <div className="dr-tiles">
      <Tile label="Invested" value={figure("capital_deployed", (v) => dealMoney(v, c))} note={why || "capital: notes, preferred, warrant exercises, shares, and the counsel paid at their closing"} />
      <Tile
        label="Cash back"
        value={figure("cash_returned_net", (v) => dealMoney(v, c))}
        tone={withheld ? undefined : "positive"}
        note={why || cashBackNote(stats, c)}
      />
      <Tile
        label="Still held"
        value={figure("exposure_remaining", (v) => dealMoney(v, c))}
        note={why || (floor ? "counted as nil — the investment accounts are below zero" : heldNote)}
      />
      <Tile
        label="P&L"
        value={figure("net_profit", (v) => dealMoney(v, c))}
        tone={withheld || floor ? undefined : toneOfValue(stats.net_profit)}
        provisional={provisional && !withheld}
        note={why || valueNote}
      />
      <Tile
        label="MOIC"
        value={figure("moic", (v) => multiple(v))}
        tone={withheld || floor ? undefined : Number(stats.moic) >= 1 ? "positive" : "negative"}
        note={why || (toNumber(stats.moic) === null ? stats.moic_reads_as || "no capital went in" : "(cash back + still held) ÷ invested")}
      />
      <Tile
        label="IRR"
        value={withheld ? "n/a" : toNumber(stats.irr_pct) === null ? "—" : pct(stats.irr_pct, 1)}
        tone={withheld || floor || toNumber(stats.irr_pct) === null ? undefined : toneOfValue(stats.irr_pct)}
        note={why || (toNumber(stats.irr_pct) === null ? "does not converge on these flows" : "annualised, on the deal's dated cash")}
      />
      <Tile
        label="Most cash tied up at once"
        value={dealMoney(summary.max_cash_exposure, currency)}
        tone="negative"
        note={summary.max_cash_exposure_date ? `on ${day(summary.max_cash_exposure_date)}` : "no cash was deployed"}
        info="The deepest point of the deal's cumulative cash curve: the most it ever had out at one time, net of what had come back. On a deal that recovered money and redeployed it, that is less than everything it invested."
      />
      <Tile
        label="First and last movement"
        wrap
        value={`${day(deal.summary.first_date)} – ${day(deal.summary.last_date)}`}
        note={
          `${deal.summary.points} ${deal.summary.points === 1 ? "day" : "days"} with cash movement` +
          (deal.deal.funding_date ? `; the deal record says funded ${day(deal.deal.funding_date)}` : "")
        }
      />
    </div>
  );
}

function LeftOut({ deal }) {
  const corrections = deal.corrections?.transactions || 0;
  const repointed = deal.repointed?.transactions || 0;
  return (
    <div className="dr-footnote">
      What these figures leave out
      <InfoTip wide>
        <p>
          Transfers between our own cash accounts and flows to and from the fund's owners: a
          bank-to-broker wire moves cash but funds nothing, and a distribution paid out of a deal's
          proceeds is not the deal consuming cash.
        </p>
        {corrections > 0 && (
          <p>
            {corrections} reversed {corrections === 1 ? "transaction is" : "transactions are"} left out,
            carrying {dealMoney(deal.corrections.cash, currencyOf(deal))} of movement. The ledger cannot
            be edited, so a correction is a second transaction and the pair stays on the book —
            counting both would add the whole amount to invested and to cash back.
          </p>
        )}
        {repointed > 0 && <p>{repointedText(deal)}</p>}
      </InfoTip>
    </div>
  );
}

function CurveCard({ deal }) {
  const { currency, converted, unconverted } = viewOf(deal);
  return (
    <Card
      title="Cumulative cash"
      subtitle={
        converted
          ? `${currency}, converted from ${deal.currency.join(" + ")} at each date's fixing`
          : unconverted
          ? `${currency} at face value — not converted`
          : currency
      }
      action={
        <InfoTip align="right" wide>
          <p>
            Money out of the fund into the deal takes the line down; money back takes it up. The red
            dot is the most cash the deal had tied up at once.
          </p>
          {showFundingRounds(deal) && (
            <p>The dashed lines mark the day each tranche's cash left (T1, T2; E1 for an equity tranche) — the Tranches tab splits the deal on them.</p>
          )}
        </InfoTip>
      }
    >
      <CashCurve deal={deal} />
    </Card>
  );
}

export function NotFound() {
  return (
    <div className="dr-page">
      <div className="dr-detail-nav">
        <Link to="/deal-reports" className="dr-back">
          ← All deals
        </Link>
      </div>
      <Card>
        <div style={{ padding: "24px 4px", fontSize: 14, color: theme.textSecondary }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: theme.text, marginBottom: 6 }}>This deal is not on Deal Reports.</div>
          Only deals with cash movement on the ledger are listed here, so a deal with none yet has no
          report; the link may also be out of date.{" "}
          <Link to="/deal-reports" className="dr-link">
            Go back to all deals
          </Link>
          .
        </div>
      </Card>
    </div>
  );
}

function StepButton({ to, label, name }) {
  if (!to) {
    return (
      <span className="dr-step is-disabled" aria-disabled="true">
        {label}
      </span>
    );
  }
  return (
    <Link to={to} className="dr-step" title={name}>
      {label}
    </Link>
  );
}

export function DealDetail({ deal, steps, nameOf, freshness }) {
  const [search, setSearch] = useSearchParams();
  const tab = tabOf(search);
  const setTab = (key) => setSearch(key === "summary" ? {} : { tab: key }, { replace: true });
  const keepTab = (id) => (id ? `${dealReportPath(id)}${tab === "summary" ? "" : `?tab=${tab}`}` : null);

  const record = deal.deal;
  const subtitle = [dealSubline(record), strategyLabel(record.strategy)].filter(Boolean).join(" · ");
  const recorded = deal.funding_rounds?.tranches_recorded || 0;

  return (
    <div className="dr-page">
      <div className="dr-detail-nav no-print">
        <Link to="/deal-reports" className="dr-back">
          ← All deals
        </Link>
        <span className="dr-steps">
          <StepButton to={keepTab(steps.previous)} label="‹ Previous" name={steps.previous ? nameOf(steps.previous) : ""} />
          {steps.position && (
            <span className="dr-steps-at">
              {steps.position} of {steps.total}
              {steps.fromList ? " in your list" : ""}
            </span>
          )}
          <StepButton to={keepTab(steps.next)} label="Next ›" name={steps.next ? nameOf(steps.next) : ""} />
        </span>
      </div>

      <PageHeader title={record.name} subtitle={subtitle} action={freshness} />

      <div className="dr-detail-bar">
        <span className="dr-detail-badges">
          <Badge text={(record.status || "").trim() || "No status"} variant={/^active$/i.test((record.status || "").trim()) ? "green" : "gray"} />
          {deal.currency_is_mixed ? (
            // Capped: a data fault that returns a long list must not be able
            // to squeeze the title out of the header.
            <Badge text={deal.currency.slice(0, 3).join(" / ")} variant="yellow" dot />
          ) : (
            <Badge text={currencyOf(deal)} variant="gray" />
          )}
          {deal.summary && !deal.summary.basis_complete && <Badge text="provisional" variant="amber" />}
        </span>
        <ToolRow>
          <Link className="pt-button no-print" to={positionsPath(record.ticker || record.name)} title="This deal's open positions on the Positions page">
            Position
          </Link>
          <Link className="pt-button no-print" to={ledgerPath(record.name)} title="Every ledger entry tagged to this deal">
            Ledger
          </Link>
          <button type="button" className="pt-button no-print" onClick={() => setTab("documents")} title="The notices filed against this deal">
            Notices
          </button>
          <Link className="pt-button no-print" to={noticeReviewPath()} title="The morning queue of notices to confirm or reject">
            Notice Review
          </Link>
          <DealPdfButton dealId={record.id} />
        </ToolRow>
      </div>

      <UnconvertedWarning deal={deal} />
      <BasisWarning deal={deal} />

      <div className="dr-tabs no-print">
        <Tabs
          tabs={DEAL_TABS.map((t) => (t.key === "tranches" && recorded > 1 ? { ...t, count: recorded } : t))}
          active={tab}
          onChange={setTab}
        />
      </div>

      {tab === "summary" && (
        <>
          <Card title="Summary" subtitle={`From the general ledger, in ${(deal.stats && deal.stats.currency) || viewOf(deal).currency}`}>
            <SummaryTiles deal={deal} />
            <LeftOut deal={deal} />
          </Card>
          <PnlBridge deal={deal} />
        </>
      )}
      {tab === "investors" && <InvestorWaterfall deal={deal} />}
      {tab === "curve" && <CurveCard deal={deal} />}
      {tab === "tranches" &&
        (showFundingRounds(deal) ? (
          <FundingRounds deal={deal} />
        ) : (
          <Card title="Tranches">
            <div style={{ fontSize: 13, color: theme.textSecondary }}>{singleTrancheText(deal)}</div>
          </Card>
        ))}
      {tab === "activity" && <DealActivity deal={deal} />}
      {tab === "documents" && <DealDocuments deal={deal} />}
    </div>
  );
}
